import React, { useState } from 'react';
import { Button, FormControl, InputLabel, Input, FormGroup, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import styles from '../../SiteManagement/styles'; //TODO EXPORT THESE HIGHER!
import { Environments, SiteDTO, CustomerDTO, ProviderSiteDTO } from '@beable/roster-backend-client-v1';
import { SUBPAGES } from "../constants";
import { ModalCustomerSelector } from '../../Common/CustomerSelector';
import { ModalSiteSelector } from '../../Common/SiteSelector';
import EnvironmentSelector from '../../Common/Selectors/EnvironmentSelector';
import ProviderSelector from './ProviderSelector';
import { InputLengthHelperText } from "../../Common/FormModal";

interface ProviderSiteFormProps {
    onSubmit: (siteDto: ProviderSiteDTO, allEnvs: boolean, env: string) => void;
    handleClose: () => void;
    selectedItem?: ProviderSiteDTO;
    site?: SiteDTO | null;
    env?: Environments | null;
}

export default function ProviderSiteForm(props: ProviderSiteFormProps) {
    const theme = useTheme();
    const styleClasses = styles.addModalStyles(theme);

    const [allEnvs, setEnableForAllEnvs] = useState(false);
    const [env, setEnv] = useState(props.env ?? "qa");
    const [ssoProviderSite, setSSOProviderSite] = useState<ProviderSiteDTO>(props.selectedItem ?? SUBPAGES.providerSites.defaultState);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDTO | undefined>(undefined);

    const checkSiteFieldVals = () => {
        return  !(
            ssoProviderSite.identifier.trim().length && 
            ssoProviderSite.providerId.length && 
            ssoProviderSite.siteName?.length && 
            ssoProviderSite.siteId
        );
    };

    const handleCustomerChange = (customer: CustomerDTO) => {
        setSelectedCustomer(customer);
        setSSOProviderSite((ssoProviderSite: ProviderSiteDTO) => { return { ...ssoProviderSite, customer: customer?.id } });
    };

    const handleSiteChange = (site: SiteDTO) => {
        setSSOProviderSite((ssoProviderSite: ProviderSiteDTO) => { return { ...ssoProviderSite, siteId: site?.id, siteName: site?.subdomain } });
    }

    const providerSiteDataChangeHandler = (e: any) => {
        const { name, value } = e.target; 

        let scrubbedValue: string = '';
        if (name === 'providerValidationString') {
            scrubbedValue = value.replace(/(?:\r\n|\r|\n|-|BEGIN CERTIFICATE|END CERTIFICATE)/g, '');
        }

        setSSOProviderSite((providerSite: ProviderSiteDTO) => { return { ...providerSite, [name]: scrubbedValue.length > 0 ? scrubbedValue : value } });
    }

    const providerSiteEnvChangeHandler = (e: any) => setEnv(e.target.value);

    const handleSetActive = (e: any) => {
        const { checked } = e.target; 
        setSSOProviderSite((providerSite: ProviderSiteDTO) => { return { ...providerSite, active: checked } });
    }

    const handleSetAllEnvs = (e: any) => {
        setEnableForAllEnvs(e.target.checked);
    }

    const resetState = () => {
        setEnableForAllEnvs(false);
        setEnv("");
        setSelectedCustomer(undefined);
        setSSOProviderSite(SUBPAGES.providerSites.defaultState);
    }

    const submitForm = async () => {
        props.onSubmit(ssoProviderSite, allEnvs, !allEnvs ? env : 'all');
    };

    const getCustomerId = () => {
        if (props.site) {
            return props.site.customerId;
        }

        return selectedCustomer?.id ?? '';
    }

    return <>
        <FormGroup>
        <FormControl variant="standard" margin="dense">
                <ModalCustomerSelector
                    onSelect={handleCustomerChange}
                    required
                    customerId={getCustomerId()}
                    disabled={!!(props.selectedItem)}
                />
            </FormControl>
            <FormControl variant="standard" margin="dense">
                <ModalSiteSelector
                    customerId={getCustomerId()}
                    siteId={props.selectedItem?.siteId ?? undefined}
                    disabled={!selectedCustomer || !!(props.selectedItem)}
                    onSelect={handleSiteChange}
                    required
                />
            </FormControl>
            <FormControl variant="standard" margin="dense">
                <ProviderSelector
                    onChange={providerSiteDataChangeHandler}
                    providerName={ssoProviderSite.providerId ?? ''}
                    disabled={!!(props.selectedItem)}
                />
            </FormControl>
            <FormControl
                variant="standard"
                margin="dense"
                sx={allEnvs ? styleClasses.dnone : styleClasses.dflex}>
                <EnvironmentSelector 
                    env={env as Environments}
                    onChange={providerSiteEnvChangeHandler}
                    disabled={!!(props.selectedItem)}
                    labelText={"Environment*"}
                />
            </FormControl>
            { !(props.selectedItem) && (
            <FormControlLabel
                control={
                    <Checkbox name='allEnvs' sx={styleClasses.cbs} checked={allEnvs} onChange={handleSetAllEnvs} />
                }
                label="Add for all environments"
            />
            ) }
            <FormControl variant="standard" margin="dense" disabled>
                <InputLabel htmlFor="providerValidation">Provider Validation</InputLabel>
                <Input onChange={providerSiteDataChangeHandler} value={ssoProviderSite.providerValidation} name="providerValidation"
                    id="providerValidation" aria-describedby="provider validation" />
            </FormControl>
            <FormControl variant="standard" margin="dense">
                <TextField
                    variant="standard"
                    label="Provider Validation String"
                    multiline
                    rows={5}
                    onChange={providerSiteDataChangeHandler}
                    value={ssoProviderSite.providerValidationString}
                    name="providerValidationString"
                    id="providerValidationString"
                    aria-describedby="provider valdation string" />
            </FormControl>
            <FormControl variant="standard" margin="dense">
                <InputLabel htmlFor="identifier">Identifier*</InputLabel>
                <Input onChange={providerSiteDataChangeHandler} name='identifier' value={ssoProviderSite.identifier} id="identifier" aria-describedby="identifier" />
                <InputLengthHelperText str={ssoProviderSite.identifier} />
            </FormControl>
            <FormControlLabel
                control={
                    <Checkbox name='active' sx={styleClasses.cbs} checked={ssoProviderSite.active} onChange={handleSetActive} />
                }
                label="Active"
            />
        </FormGroup>
        {/* BUTTONS */}
        <FormGroup sx={[styleClasses.mt2, styleClasses.dfr]}>
            <Button variant="contained" color="primary" disabled={checkSiteFieldVals()}
                onClick={submitForm}>{ (props.selectedItem ? "Edit" : "Add site") }</Button>
            <Button
                variant="contained"
                onClick={() => {
                    resetState();
                    props.handleClose();
                }}
                sx={styleClasses.ml2}>Close</Button>
        </FormGroup>
    </>;
}